import { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faImage,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import type { AboutMediaItem } from "./about-media";

type AboutMediaCarouselLightboxProps = {
  items: readonly AboutMediaItem[];
  initialIndex: number;
  open: boolean;
  onClose: () => void;
};

/** Full-screen carousel lightbox for feature screenshots and clips. */
export default function AboutMediaCarouselLightbox({
  items,
  initialIndex,
  open,
  onClose,
}: AboutMediaCarouselLightboxProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<Map<number, HTMLVideoElement>>(new Map());
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const reduceMotion =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (open) setActiveIndex(initialIndex);
  }, [open, initialIndex]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  const pauseAllVideos = useCallback(() => {
    videoRefs.current.forEach((video) => video.pause());
  }, []);

  const playActiveVideo = useCallback(
    (index: number) => {
      pauseAllVideos();
      const item = items[index];
      if (!item?.videoSrc || reduceMotion) return;
      const video = videoRefs.current.get(index);
      if (!video) return;
      void video.play().catch(() => {
        /* autoplay blocked */
      });
    },
    [items, pauseAllVideos, reduceMotion],
  );

  const scrollToIndex = useCallback(
    (index: number) => {
      const track = trackRef.current;
      if (!track) return;
      const slide = track.children[index] as HTMLElement | undefined;
      if (!slide) return;
      slide.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "nearest",
        inline: "center",
      });
      setActiveIndex(index);
      playActiveVideo(index);
    },
    [playActiveVideo, reduceMotion],
  );

  useEffect(() => {
    if (!open) return;
    const track = trackRef.current;
    if (!track) return;

    const onScroll = () => {
      const { scrollLeft, clientWidth } = track;
      const index = Math.round(scrollLeft / clientWidth);
      const next = Math.min(Math.max(index, 0), items.length - 1);
      if (next !== activeIndex) {
        setActiveIndex(next);
        playActiveVideo(next);
      }
    };

    track.addEventListener("scroll", onScroll, { passive: true });
    return () => track.removeEventListener("scroll", onScroll);
  }, [open, items.length, activeIndex, playActiveVideo]);

  useEffect(() => {
    if (!open) return;
    const id = window.requestAnimationFrame(() => scrollToIndex(initialIndex));
    return () => window.cancelAnimationFrame(id);
  }, [open, initialIndex, scrollToIndex]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") scrollToIndex(Math.max(activeIndex - 1, 0));
      if (event.key === "ArrowRight") scrollToIndex(Math.min(activeIndex + 1, items.length - 1));
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, activeIndex, items.length, scrollToIndex]);

  const onDialogClose = () => {
    pauseAllVideos();
    onClose();
  };

  const item = items[activeIndex];

  return (
    <dialog
      ref={dialogRef}
      className="about-media-lightbox about-media-carousel-lightbox"
      onClose={onDialogClose}
      onCancel={(event) => {
        event.preventDefault();
        onDialogClose();
      }}
      onClick={(event) => {
        if (event.target === dialogRef.current) onDialogClose();
      }}
    >
      <div className="about-media-lightbox-inner about-media-carousel-lightbox-inner" role="document">
        <button
          type="button"
          className="about-media-lightbox-close"
          onClick={onDialogClose}
          aria-label="Close gallery"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>

        <div className="about-media-carousel-lightbox-toolbar">
          <button
            type="button"
            className="about-carousel-arrow about-media-carousel-lightbox-arrow"
            onClick={() => scrollToIndex(Math.max(activeIndex - 1, 0))}
            disabled={activeIndex === 0}
            aria-label="Previous screenshot"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <p className="about-media-carousel-lightbox-counter" aria-live="polite">
            {activeIndex + 1} / {items.length}
          </p>
          <button
            type="button"
            className="about-carousel-arrow about-media-carousel-lightbox-arrow"
            onClick={() => scrollToIndex(Math.min(activeIndex + 1, items.length - 1))}
            disabled={activeIndex === items.length - 1}
            aria-label="Next screenshot"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>

        <div
          ref={trackRef}
          className="about-media-carousel-lightbox-track"
          role="region"
          aria-roledescription="carousel"
          aria-label="Feature screenshots"
        >
          {items.map((slide, index) => (
            <div
              key={slide.id}
              className="about-media-carousel-lightbox-slide"
              aria-hidden={index !== activeIndex}
            >
              <div className="about-media-lightbox-stage">
                {slide.videoSrc && !reduceMotion ? (
                  <video
                    ref={(el) => {
                      if (el) videoRefs.current.set(index, el);
                      else videoRefs.current.delete(index);
                    }}
                    className="about-media-lightbox-video"
                    src={slide.videoSrc}
                    poster={slide.imageSrc}
                    controls
                    playsInline
                    muted
                  />
                ) : slide.imageSrc ? (
                  <img className="about-media-lightbox-image" src={slide.imageSrc} alt="" />
                ) : (
                  <div className="about-media-lightbox-missing">
                    <FontAwesomeIcon icon={faImage} />
                    <p>{slide.placeholderLabel}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {item ? <p className="about-media-lightbox-caption">{item.caption}</p> : null}

        <div className="about-carousel-dots about-media-carousel-lightbox-dots" role="tablist" aria-label="Choose a screenshot">
          {items.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              role="tab"
              className="about-carousel-dot about-media-carousel-lightbox-dot"
              aria-selected={index === activeIndex}
              aria-label={slide.caption}
              onClick={() => scrollToIndex(index)}
            />
          ))}
        </div>
      </div>
    </dialog>
  );
}
